/**
 * InstallBanner Component
 * 
 * Shows a subtle banner prompting users to install the PWA.
 * - Only shows when install is available
 * - Can be dismissed (won't show for a week)
 * - Shows iOS-specific instructions
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, Share } from 'lucide-react';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { trackEvent } from '@/services/telemetry/telemetry';

const DISMISS_KEY = 'trueprice.installBannerDismissed.v1';

export function InstallBanner() {
  const { canInstall, isIOS, isInstalled, promptInstall, dismissPrompt } = useInstallPrompt();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === 'true'; } catch { return false; }
  });

  const shouldShow = !isInstalled && !dismissed && (canInstall || isIOS);

  useEffect(() => {
    if (shouldShow) {
      trackEvent({ eventType: 'pwa_install_banner_shown', screen: 'app', meta: { isIOS } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't render if installed, dismissed, or can't install
  if (!shouldShow) return null;

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
    } else {
      await promptInstall();
    }
  };

  return (
    <>
      <AnimatePresence>
        {(canInstall || isIOS) && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-4 right-4 z-40 max-w-lg mx-auto"
            style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
          >
            <div className="bg-cream-100 rounded-2xl shadow-lg border border-charcoal-100 p-4">
              {/* Top row: icon + text + dismiss */}
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <Home className="w-6 h-6 text-teal-600" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-charcoal-800 text-sm leading-snug">
                    Put TruePrice on your home screen
                  </h3>
                  <p className="text-xs text-charcoal-500 mt-0.5 leading-snug">
                    Full screen. Offline. One tap to open.
                  </p>
                </div>

                {/* Dismiss */}
                <button
                  onClick={() => {
                    try { localStorage.setItem(DISMISS_KEY, 'true'); } catch {}
                    setDismissed(true);
                    trackEvent({ eventType: 'pwa_install_banner_dismissed', screen: 'app', meta: { isIOS } });
                    dismissPrompt();
                  }}
                  className="w-8 h-8 rounded-lg hover:bg-charcoal-100 flex items-center justify-center transition-colors flex-shrink-0 -mt-1 -mr-1"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4 text-charcoal-400" />
                </button>
              </div>

              {/* Bottom row: right-aligned pill CTA */}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={handleInstallClick}
                  className="px-5 py-2 bg-teal-500 text-white text-sm font-semibold rounded-full hover:bg-teal-600 active:bg-teal-700 transition-colors"
                >
                  {isIOS ? 'How to install' : 'Install'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS Instructions Modal */}
      <AnimatePresence>
        {showIOSInstructions && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowIOSInstructions(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-x-4 bottom-8 z-50 max-w-sm mx-auto"
            >
              <div className="bg-cream-100 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-charcoal-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-charcoal-800">
                      Two taps to install
                    </h2>
                    <button
                      onClick={() => setShowIOSInstructions(false)}
                      className="w-8 h-8 rounded-full hover:bg-charcoal-100 flex items-center justify-center"
                    >
                      <X className="w-5 h-5 text-charcoal-400" />
                    </button>
                  </div>
                </div>

                {/* Steps */}
                <div className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-teal-700">1</span>
                    </div>
                    <div>
                      <p className="text-sm text-charcoal-700">
                        Tap the <Share className="w-4 h-4 inline text-blue-500 -mt-0.5" /> Share button in Safari
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-teal-700">2</span>
                    </div>
                    <div>
                      <p className="text-sm text-charcoal-700">
                        Scroll down and tap <strong>"Add to Home Screen"</strong>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-teal-700">3</span>
                    </div>
                    <div>
                      <p className="text-sm text-charcoal-700">
                        Tap <strong>"Add"</strong> to install
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-5 pb-5">
                  <button
                    onClick={() => setShowIOSInstructions(false)}
                    className="w-full py-3 bg-charcoal-100 text-charcoal-700 font-semibold rounded-xl hover:bg-charcoal-200 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default InstallBanner;
